import { Children, useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ReferralCprBadge } from '@/components/referrals/ReferralCprBadge';
import { referralLetterFrameClass } from '@/components/referrals/ReferralLetterDetails';
import { Card } from '@/components/ui/Card';
import type { Referral } from '@/hooks/useReferrals';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { cn } from '@/utils/cn';
import { referralOverviewRuleClass } from './referralOverviewLayout';

const STORAGE_KEY = 'clinio-v2-referral-overview-collapsed';

/**
 * Henvisningsoplysninger: the same fields and collapse as on the standalone
 * henvisning page, in the same card as the patient Henvisning box — an outer
 * card and the letter frame inside it.
 */
export function ReferralOverviewCard({
  referral,
  canRevealCpr,
}: {
  referral: Referral;
  canRevealCpr: boolean;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  return (
    <Card className="mt-4" data-testid="referral-overview">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? t('referrals.overview_expand') : t('referrals.overview_collapse')}
        className="flex min-h-10 w-full items-center justify-between text-left hover:bg-ds-row-hover"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">
          {t('referrals.overview_title')}
        </span>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-ds-muted" />
        ) : (
          <ChevronUp className="h-4 w-4 text-ds-muted" />
        )}
      </button>
      {!collapsed && (
        <div className="mt-s4">
          <div className={referralLetterFrameClass} data-testid="referral-overview-frame">
            <OverviewGrid>
              <OverviewField label={t('referrals.section_sender')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{referral.sender_hospital ?? '—'}</div>
                  <div className="text-xs text-ds-muted">{referral.sender_department ?? '—'}</div>
                  <div className="text-xs text-ds-muted">
                    {formatReferralDateTime(referral.sent_at) ?? '—'}
                  </div>
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_referring_doctor')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{referral.referring_doctor_name ?? '—'}</div>
                  <div className="text-xs text-ds-muted">
                    {referral.referring_doctor_sor ? `SOR ${referral.referring_doctor_sor}` : '—'}
                  </div>
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_patient')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    {referral.first_name} {referral.last_name}
                  </div>
                  {referral.has_cpr && (
                    <ReferralCprBadge
                      referralId={referral.id}
                      subject="patient"
                      canReveal={canRevealCpr}
                    />
                  )}
                  <div className="text-xs text-ds-muted">{referral.phone ?? '—'}</div>
                  <div className="text-xs text-ds-muted">{referral.email ?? '—'}</div>
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_parents')}>
                <div className="space-y-2">
                  <ParentLine
                    name={referral.parent_1_name}
                    hasCpr={referral.has_parent_1_cpr}
                    cprBadge={
                      <ReferralCprBadge
                        referralId={referral.id}
                        subject="parent_1"
                        canReveal={canRevealCpr}
                      />
                    }
                  />
                  <ParentLine
                    name={referral.parent_2_name}
                    hasCpr={referral.has_parent_2_cpr}
                    cprBadge={
                      <ReferralCprBadge
                        referralId={referral.id}
                        subject="parent_2"
                        canReveal={canRevealCpr}
                      />
                    }
                  />
                  {!referral.parent_1_name &&
                    !referral.parent_2_name &&
                    !referral.has_parent_1_cpr &&
                    !referral.has_parent_2_cpr && <div className="text-xs text-ds-muted">—</div>}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.field_ambulant')}>
                <div className="text-sm font-medium">
                  {referral.ambulant === true
                    ? t('referrals.ambulant_yes')
                    : referral.ambulant === false
                      ? t('referrals.ambulant_no')
                      : '—'}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_referrer')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{referral.referrer_name ?? '—'}</div>
                  <div className="text-xs text-ds-muted">
                    {referral.referrer_type
                      ? t(`referrals.referrer_type_${referral.referrer_type}`)
                      : '—'}
                  </div>
                  {referral.referrer_reference && (
                    <div className="text-xs text-ds-muted">Ref: {referral.referrer_reference}</div>
                  )}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_details')}>
                <div className="space-y-1">
                  {referral.reason && (
                    <div className="text-xs text-ds-muted">{referral.reason}</div>
                  )}
                  {referral.notes && <div className="text-xs text-ds-muted">{referral.notes}</div>}
                  {!referral.reason && !referral.notes && (
                    <div className="text-xs text-ds-muted">—</div>
                  )}
                </div>
              </OverviewField>
              <OverviewField label="Administrativ information">
                <div className="space-y-1">
                  {referral.secretary_name && (
                    <div className="text-xs text-ds-muted">Sekr: {referral.secretary_name}</div>
                  )}
                  {referral.psychologist_name && (
                    <div className="text-xs text-ds-muted">Psyk: {referral.psychologist_name}</div>
                  )}
                  {referral.id_number && (
                    <div className="text-xs text-ds-muted">ID: {referral.id_number}</div>
                  )}
                  {referral.case_number && (
                    <div className="text-xs text-ds-muted">Sag: {referral.case_number}</div>
                  )}
                  {!referral.secretary_name &&
                    !referral.psychologist_name &&
                    !referral.id_number &&
                    !referral.case_number && <div className="text-xs text-ds-muted">—</div>}
                </div>
              </OverviewField>
            </OverviewGrid>
          </div>
        </div>
      )}
    </Card>
  );
}

function OverviewGrid({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4">
      {items.map((child, index) => (
        <div
          key={index}
          className={cn('min-w-0 px-6 py-5', referralOverviewRuleClass(index, items.length))}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

function OverviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">{label}</span>
      </div>
      <div className="text-sm text-ds-text">{children}</div>
    </div>
  );
}

function ParentLine({
  name,
  hasCpr,
  cprBadge,
}: {
  name: string | null;
  hasCpr: boolean;
  cprBadge: ReactNode;
}) {
  if (!name && !hasCpr) return null;
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">{name ?? '—'}</div>
      {hasCpr && cprBadge}
    </div>
  );
}
